import { useEffect, useMemo, useRef, useState } from 'react';
import {
  cardId,
  hintsForCard,
  missionRuleDescriptions,
  type Card,
  type ClientSnapshot,
  type GameView,
  type TaskState
} from '@crew/shared';
import type { Crew } from '../net.js';
import { useCardDrag, useHandOrder } from '../hand.js';
import { sfx } from '../sound.js';
import { useTableActivity } from '../useTableActivity.js';
import { Avatar } from './Avatar.js';
import { PlayingCard } from './PlayingCard.js';

const SUIT_WORD: Record<string, string> = {
  pink: 'pink',
  green: 'green',
  blue: 'blue',
  yellow: 'yellow',
  rocket: 'rocket'
};

export function GameTable({ crew, snapshot }: { crew: Crew; snapshot: ClientSnapshot }) {
  const game = snapshot.game as GameView;
  const [signalling, setSignalling] = useState(false);
  const [confirmQuit, setConfirmQuit] = useState(false);
  const [resultDismissed, setResultDismissed] = useState(false);
  const [goalOpen, setGoalOpen] = useState(
    () => window.localStorage.getItem('crew-online:goal-open') === '1'
  );

  const toggleGoal = () => {
    setGoalOpen((open) => {
      const next = !open;
      try {
        window.localStorage.setItem('crew-online:goal-open', next ? '1' : '0');
      } catch {
        /* private browsing */
      }
      return next;
    });
  };

  const names = useMemo(() => {
    const map = new Map<string, { name: string; avatar: string; avatarColor: string; mood: string }>();
    for (const p of snapshot.room.players) {
      map.set(p.id, {
        name: p.name,
        avatar: p.avatar,
        avatarColor: p.avatarColor,
        mood: p.mood
      });
    }
    return map;
  }, [snapshot.room.players]);
  const nameOf = (id: string | null) => (id ? (names.get(id)?.name ?? 'Pilot') : '');
  const faceOf = (id: string) =>
    (names.get(id)?.avatar ?? 'pilot') as Parameters<typeof Avatar>[0]['id'];
  const tintOf = (id: string) =>
    (names.get(id)?.avatarColor ?? 'blue') as NonNullable<Parameters<typeof Avatar>[0]['color']>;
  const moodOf = (id: string) =>
    (names.get(id)?.mood ?? 'happy') as NonNullable<Parameters<typeof Avatar>[0]['mood']>;

  const you = game.you;
  const myTurn = you ? game.turnId === you.id : false;
  const legal = new Set(you?.legalCardIds ?? []);
  const communicable = new Set(you?.communicableCardIds ?? []);
  const over = game.phase === 'won' || game.phase === 'lost';
  const offline = game.players.filter((p) => !p.connected);
  const mine = game.tasks.filter((t) => t.ownerId === you?.id);
  const myOpen = mine.filter((t) => t.completedAt === null);
  const { cards: handCards, move, sort: sortHandOrder, isSorted } = useHandOrder(you?.hand ?? []);
  const drag = useCardDrag(move);
  const { activity, recap } = useTableActivity(game);
  const moveKey = `${game.phase}:${game.trickNumber}:${game.currentTrick.length}:${game.turnId}`;
  const pendingPlay = useRef<{ key: string } | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const playPending = pendingKey === moveKey;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Sound follows state transitions, so it fires once per real event.
  const previous = useRef({
    trick: game.trickNumber,
    tasks: game.completedTasks,
    phase: game.phase,
    myTurn,
    plays: game.currentTrick.length,
    turnId: game.turnId,
    attempt: game.attempt
  });

  useEffect(() => {
    const was = previous.current;
    if (game.currentTrick.length > was.plays || game.trickNumber > was.trick) sfx.play('play');
    if (game.trickNumber > was.trick) sfx.play('trick');
    if (game.completedTasks > was.tasks) sfx.play('task');
    if (game.attempt !== was.attempt) sfx.play('deal');
    if (game.phase !== was.phase) {
      if (game.phase === 'won') sfx.play('win');
      if (game.phase === 'lost') sfx.play('fail');
    }
    const live = game.phase !== 'won' && game.phase !== 'lost';
    if (myTurn && !was.myTurn && live) sfx.play('turn');
    // A quieter blip so you can hear the table moving even when it is not you.
    else if (live && game.turnId !== was.turnId) sfx.play('pass');
    previous.current = {
      trick: game.trickNumber,
      tasks: game.completedTasks,
      phase: game.phase,
      myTurn,
      plays: game.currentTrick.length,
      turnId: game.turnId,
      attempt: game.attempt
    };
  }, [
    game.trickNumber,
    game.completedTasks,
    game.phase,
    game.currentTrick.length,
    game.turnId,
    game.attempt,
    myTurn
  ]);

  // Reopen the result if a new mission ends while it is dismissed.
  useEffect(() => {
    if (game.phase === 'won' || game.phase === 'lost') setResultDismissed(false);
  }, [game.phase, game.attempt]);

  const onCardClick = (card: Card) => {
    const id = cardId(card);
    if (signalling) {
      const hint = hintsForCard(you?.hand ?? [], card, game.houseRules.relaxedComms)[0];
      if (!hint) return;
      setSignalling(false);
      sfx.play('hint');
      void crew.communicate(id, hint);
      return;
    }
    if (!myTurn || !legal.has(id) || over || pendingPlay.current?.key === moveKey) return;
    const request = { key: moveKey };
    pendingPlay.current = request;
    setPendingKey(moveKey);
    // A new authoritative move key immediately permits legitimate input, even
    // if the old acknowledgement is late. A failed request permits a retry.
    void crew.playCard(id).then((accepted) => {
      if (!accepted && mounted.current && pendingPlay.current === request) {
        pendingPlay.current = null;
        setPendingKey(null);
      }
    });
  };

  const [bubbles, setBubbles] = useState<Record<string, { id: number; text: string }>>({});
  const bubbleSeq = useRef(0);
  const seenChat = useRef<number>(-1);
  const bubbleTimers = useRef(new Map<string, number>());
  useEffect(() => () => {
    bubbleTimers.current.forEach(window.clearTimeout);
    bubbleTimers.current.clear();
  }, []);

  const say = (playerId: string, text: string) => {
    const id = ++bubbleSeq.current;
    setBubbles((b) => ({ ...b, [playerId]: { id, text } }));
    window.clearTimeout(bubbleTimers.current.get(playerId));
    bubbleTimers.current.set(playerId, window.setTimeout(() => {
      bubbleTimers.current.delete(playerId);
      setBubbles((b) => {
        if (b[playerId]?.id !== id) return b;
        const next = { ...b };
        delete next[playerId];
        return next;
      });
    }, 4200));
  };

  // Chat surfaces at the speaker's seat as well as in the side panel.
  useEffect(() => {
    const latest = snapshot.chat[snapshot.chat.length - 1];
    if (!latest) return;
    if (seenChat.current < 0) {
      seenChat.current = latest.id;
      return;
    }
    if (latest.id === seenChat.current) return;
    seenChat.current = latest.id;
    if (latest.playerId) say(latest.playerId, latest.text);
  }, [snapshot.chat]);

  const quipPrev = useRef({ plays: game.currentTrick.length, trick: game.trickNumber });  useEffect(() => {
    const was = quipPrev.current;
    if (game.currentTrick.length > was.plays) {
      const last = game.currentTrick[game.currentTrick.length - 1];
      if (last && Math.random() < 0.24) say(last.playerId, pick(QUIPS_PLAY));
    }
    if (game.trickNumber > was.trick && game.lastTrick && Math.random() < 0.4) {
      say(game.lastTrick.winnerId, pick(QUIPS_WIN));
    }
    quipPrev.current = { plays: game.currentTrick.length, trick: game.trickNumber };
  }, [game.currentTrick.length, game.trickNumber]);

  const [poked, setPoked] = useState<string | null>(null);
  const seenPoke = useRef(crew.lastPoke);
  useEffect(() => {
    const hit = crew.lastPoke;
    // The connection retains the last poke across attempts; do not replay it.
    if (!hit || hit === seenPoke.current) return;
    seenPoke.current = hit;
    setPoked(hit.toId);
    if (hit.toId === you?.id) sfx.play('click');
    say(hit.toId, hit.toId === you?.id ? pick(QUIPS_POKED) : pick(QUIPS_POKER));
    const timer = window.setTimeout(() => setPoked(null), 900);
    return () => window.clearTimeout(timer);
  }, [crew.lastPoke]);

  // Rotate the crew so you always sit at the bottom of the table.
  const meAt = game.players.findIndex((p) => p.id === you?.id);
  const seatOrder =
    meAt >= 0
      ? [...game.players.slice(meAt + 1), ...game.players.slice(0, meAt), game.players[meAt]!]
      : game.players;

  const [statusLabel, statusTone] = ((): [string, string] => {
    if (game.phase === 'won') return ['Mission complete', 'won'];
    if (game.phase === 'lost') return ['Mission failed', 'lost'];
    if (game.phase === 'draft') return ['Choosing tasks', 'draft'];
    if (myTurn) return ['Your turn', 'you'];
    if (game.currentTrick.length === 0) return [`New trick, ${nameOf(game.turnId)} leads`, 'wait'];
    return [`Trick in play, ${nameOf(game.turnId)} to go`, 'wait'];
  })();

  // How many crewmates still play after you in this trick.
  const afterYou = Math.max(0, game.players.length - game.currentTrick.length - 1);

  /** Why a card is greyed out, so a mission rule never looks like a broken app. */
  const blockedReason = (card: Card): string => {
    if (over) return 'The mission is over.';
    if (!myTurn) return `Waiting for ${nameOf(game.turnId)}.`;
    const led = game.ledSuit;
    if (led !== null && card.suit !== led && (you?.hand ?? []).some((c) => c.suit === led)) {
      return `You still hold ${SUIT_WORD[led]}, so you have to follow it.`;
    }
    return 'Not playable right now.';
  };

  return (
    <div className="table">
      <header className="hud">
        <div className="hud__mission">
          <span className="hud__badge">Mission {game.mission.id}</span>
          <span className={`hud__phase hud__phase--${statusTone}`} role="status">
            {statusLabel}
          </span>
          <h2>{game.mission.name}</h2>
          <p>{game.mission.brief}</p>
        </div>
        <dl className="hud__readout">
          <div>
            <dt>Trick</dt>
            <dd>
              {Math.min(game.trickNumber, game.totalTricks)}
              <span>/{game.totalTricks}</span>
            </dd>
          </div>
          <div>
            <dt>Tasks</dt>
            <dd>
              {game.completedTasks}
              <span>/{game.tasks.length}</span>
            </dd>
          </div>
          <div>
            <dt>Attempt</dt>
            <dd>{game.attempt}</dd>
          </div>
        </dl>
      </header>

      <div className="table-activity" role="status" aria-live="polite" aria-atomic="true">
        <div key={activity?.id ?? 'ready'} className={activity ? 'table-activity__event' : ''}>
          {activity?.plays.map((play) => (
            <span key={`${play.playerId}:${cardId(play.card)}`}>
              <strong>{nameOf(play.playerId)}</strong> played {play.card.suit} {play.card.rank}.{' '}
            </span>
          ))}
          {activity?.tasks.map(({ task, playerId, action }) => (
            <span key={task.id}><strong>{nameOf(playerId)}</strong> {action} {task.card.suit} {task.card.rank} task.{' '}</span>
          ))}
          {recap ? <span><strong>{nameOf(recap.trick.winnerId)}</strong> won trick {recap.trick.number}. </span> : null}
        </div>
        <strong key={moveKey} className="table-activity__turn" data-turn-id={game.turnId}>
          {over ? statusLabel : `${nameOf(game.turnId)}${myTurn ? ' (you)' : ''} — ${game.phase === 'draft' ? 'choose tasks' : 'play now'}`}
        </strong>
      </div>

      <div className="table__board">
      <section className="goal" aria-label="Mission rules">
        <h3>Mission rules</h3>
        <p>Win every task card for its owner and satisfy all mission rules to succeed.</p>
        <ul>
          {missionRuleDescriptions(game.mission).map((rule) => <li key={rule}>{rule}</li>)}
        </ul>
      </section>
      {game.phase === 'draft' ? (
      <div className="goal">
        <button
          type="button"
          className="goal__toggle"
          aria-expanded={goalOpen}
          onClick={toggleGoal}
        >
          <span aria-hidden="true">{goalOpen ? '\u25BE' : '\u25B8'}</span> What is the goal?
        </button>
        {goalOpen ? (
          <p className="goal__body">
            Every task card must be won by the pilot who claimed it. Complete all{' '}
            {game.tasks.length} and satisfy the mission rules to win together. If anyone else takes
            one, the mission is over. You may not describe your hand. Each pilot gets at most one
            hint, subject to the mission's comms rules.
          </p>
        ) : null}
      </div>
      ) : null}

      {offline.length > 0 && !over ? (
        <div className="alert alert--warn" role="status">
          <span>
            {offline.map((p) => nameOf(p.id)).join(' and ')}{' '}
            {offline.length === 1 ? 'has' : 'have'} lost connection. If they do not come back, quit
            the mission to get everyone back to the lobby.
          </span>
          <button className="btn btn--small" onClick={() => setConfirmQuit(true)}>
            Quit mission
          </button>
        </div>
      ) : null}

      {game.phase === 'draft' ? (
        <TaskBoard game={game} nameOf={nameOf} youId={you?.id ?? null} crew={crew} />
      ) : null}

      <div className={`arena arena--${game.players.length}`}>
        <section className="stage" aria-label="Current trick">
        <div className="stage__label">
          {game.currentTrick.length === 0 && game.lastTrick
            ? `Trick ${game.lastTrick.number}: ${nameOf(game.lastTrick.winnerId)} took it`
            : game.ledSuit
              ? `Following ${SUIT_WORD[game.ledSuit]}`
              : 'Awaiting the lead'}
        </div>

        <div className="stage__cards">
          {(game.currentTrick.length > 0 ? game.currentTrick : (game.lastTrick?.plays ?? [])).map(
            (play) => {
              const isWinner =
                game.currentTrick.length === 0 && game.lastTrick?.winnerId === play.playerId;
              return (
                <div
                  key={`${game.currentTrick.length ? game.trickNumber : game.lastTrick?.number}:${play.playerId}:${cardId(play.card)}`}
                  className={`stage__play ${isWinner ? 'is-winner' : ''}`}
                  data-player-id={play.playerId}
                  aria-label={`${nameOf(play.playerId)} played ${play.card.suit} ${play.card.rank}`}
                >
                  <PlayingCard card={play.card} size="lg" />
                  <span className="stage__who">
                    <Avatar
                      id={faceOf(play.playerId)}
                      color={tintOf(play.playerId)}
                      mood={moodOf(play.playerId)}
                      size={26}
                    />
                    {nameOf(play.playerId)}
                  </span>
                </div>
              );
            }
          )}
          {game.currentTrick.length === 0 && !game.lastTrick ? (
            <p className="stage__empty">The commander leads the first trick.</p>
          ) : null}
        </div>

        {!over ? (
          myTurn ? (
            <div className="yourturn" role="status">
              <span className="yourturn__sweep" aria-hidden="true" />
              <strong>Your turn</strong>
              <span className="yourturn__sub">
                {game.phase === 'draft'
                  ? 'Claim a task'
                  : afterYou === 0
                    ? 'Play a card, you close the trick'
                    : `Play a card, ${afterYou} ${afterYou === 1 ? 'plays' : 'play'} after you`}
              </span>
            </div>
          ) : (
            <div className="turn" role="status">
              <WaitingDots />
              <span>
                Waiting for <strong>{nameOf(game.turnId)}</strong>
                {game.phase === 'draft' ? ' to claim a task' : null}
              </span>
            </div>
          )
        ) : null}
      </section>

      {recap ? (
        <section className={`trick-recap trick-recap--${recap.phase}`} aria-label={`Trick ${recap.trick.number} recap`}>
          <strong>Trick {recap.trick.number} · {nameOf(recap.trick.winnerId)} won</strong>
          <div className="trick-recap__cards">
            {recap.trick.plays.map((play) => (
              <div key={play.playerId} className={play.playerId === recap.trick.winnerId ? 'is-winner' : ''}>
                <PlayingCard card={play.card} size="sm" />
                <span>{nameOf(play.playerId)} played {play.card.suit} {play.card.rank}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="seats" aria-label="Crew status">
        {seatOrder.map((p) => {
          const theirs = game.tasks.filter((t) => t.ownerId === p.id);
          const open = theirs.filter((t) => t.completedAt === null);
          return (
            <article
              key={p.id}
              data-player-id={p.id}
              className={[
                'seat',
                p.isTurn && !over ? 'is-turn' : '',
                p.id === you?.id ? 'is-you' : '',
                p.connected ? '' : 'is-offline',
                poked === p.id ? 'is-poked' : '',
                theirs.length >= 3 ? 'is-wide' : '',
                open.length === 0 && theirs.length > 0 ? 'is-clear' : ''
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <header>
                <Avatar
                  id={faceOf(p.id)}
                  color={tintOf(p.id)}
                  mood={moodOf(p.id)}
                  size={52}
                  className="seat__avatar"
                />
                <span className="seat__name">{nameOf(p.id)}</span>
                {p.isTurn && !over ? <span className="chip chip--turn">TURN</span> : null}
                {p.isCommander ? <span className="chip chip--cmd">CMDR</span> : null}
                {p.connected ? null : <span className="chip chip--off">OFFLINE</span>}
              </header>

              {bubbles[p.id] ? (
                <span key={bubbles[p.id]!.id} className="bubble">
                  {bubbles[p.id]!.text}
                </span>
              ) : null}

              <div className="seat__meta">
                <span>
                  {p.handCount} {p.handCount === 1 ? 'card' : 'cards'}
                </span>
                <span>
                  {p.tricksWon} {p.tricksWon === 1 ? 'trick' : 'tricks'}
                </span>
              </div>

              <div className="seat__tasks">
                {theirs.length === 0 ? null : (
                  <span
                    className="seat__taskcards"
                    role="group"
                    aria-label={`${nameOf(p.id)} needs to win ${open.length} of ${theirs.length}`}
                  >
                    <span className="seat__tag seat__tag--task">Tasks</span>
                    {theirs.map((t) => (
                      <span
                        key={`${t.id}:${t.ownerId}:${t.completedAt}`}
                        className={`pod-card pod-card--task ${t.completedAt !== null ? 'is-done' : ''} ${activity?.tasks.some(({ task }) => task.id === t.id) ? 'is-updated' : ''}`}
                        role="group"
                        aria-label={`${t.card.suit} ${t.card.rank} task${t.constraint.kind === 'sequence' ? `, order ${t.constraint.index}` : t.constraint.kind === 'final' ? ', must finish last' : ''}, ${t.completedAt !== null ? 'complete' : 'outstanding'}`}
                      >
                        <PlayingCard
                          card={t.card}
                          size="sm"
                          badge={
                            t.constraint.kind === 'sequence'
                              ? `#${t.constraint.index}`
                              : t.constraint.kind === 'final'
                                ? 'last'
                                : null
                          }
                        />
                        {t.completedAt !== null ? (
                          <span className="pod-card__done" aria-label="task complete">
                            ✓ Done
                          </span>
                        ) : null}
                      </span>
                    ))}
                  </span>
                )}
              </div>

              <div className="seat__signal">
                {p.communication && !p.communication.played ? (
                  <>
                    <span className="seat__tag seat__tag--hint">Hint</span>
                    <span className="pod-card pod-card--hint">
                      <PlayingCard
                        card={p.communication.card}
                        size="sm"
                        muted={p.communication.played}
                      />
                    </span>
                    <span className={`hint-chip hint-chip--${shortHint(p.communication.hint)}`}>
                      {p.communication.hint}
                    </span>
                  </>
                ) : (
                  <span className="hint-state">
                    <span
                      className={`hint-dot ${p.communicationUsed ? 'is-spent' : ''}`}
                      aria-hidden="true"
                    />
                    <span className="hint-state__label">
                      {p.communicationUsed ? 'hint used' : 'hint ready'}
                    </span>
                  </span>
                )}
              </div>

              {p.id === you?.id ? null : (
                <button
                  type="button"
                  className="seat__poke"
                  title={`Poke ${nameOf(p.id)}`}
                  aria-label={`Poke ${nameOf(p.id)}`}
                  onClick={() => void crew.poke(p.id)}
                >
                  poke
                </button>
              )}
            </article>
          );
        })}
      </section>
      </div>
      </div>

      {you ? (
        <section className={`hand ${signalling ? 'is-signalling' : ''}`} aria-label="Your hand">
          <div className="hand__bar">
            <h3>Your hand</h3>
            <span className="hand__turn">{playPending ? 'Sending card…' : over ? statusLabel : `${nameOf(game.turnId)}${myTurn ? ' (you)' : ''} to ${game.phase === 'draft' ? 'choose' : 'play'}`}</span>
            <button
              type="button"
              className="btn btn--small btn--ghost"
              onClick={sortHandOrder}
              disabled={isSorted}
              title={isSorted ? 'Already sorted by colour' : 'Sort by colour, low to high'}
            >
              Sort
            </button>
            <button
              type="button"
              className={`btn btn--small ${signalling ? 'btn--primary' : ''}`}
              onClick={() => setSignalling((s) => !s)}
              disabled={!you.canCommunicate}
              title={you.communicationBlockedReason ?? 'Reveal one card as a hint'}
            >
              {signalling ? 'Pick a card to signal' : 'Give a hint'}
            </button>
            {signalling ? (
              <button className="btn btn--small btn--ghost" onClick={() => setSignalling(false)}>
                Cancel
              </button>
            ) : null}
            {you.communicationBlockedReason && !signalling ? (
              <span className="hand__note">{you.communicationBlockedReason}</span>
            ) : null}
          </div>
          <div className="hand__cards" tabIndex={0} role="group" aria-label="Your cards">
            {handCards.map((card) => {
              const id = cardId(card);
              const playable = myTurn && legal.has(id) && !over && !playPending;
              const signallable = signalling && communicable.has(id);
              const active = signalling ? signallable : playable;
              return (
                <div
                  key={id}
                  data-card-id={id}
                  className={`hand__slot ${drag.draggingId === id ? 'is-dragging' : ''}`}
                  onPointerDown={drag.onPointerDown(id)}
                >
                  <PlayingCard
                    card={card}
                    size="lg"
                    muted={!active}
                    highlight={active}
                    selected={signallable}
                    onClick={() => {
                      if (drag.wasDrag()) return;
                      onCardClick(card);
                    }}
                    disabled={!active}
                    title={
                      signalling
                        ? signallable
                          ? `Signal as your ${hintsForCard(you.hand, card, game.houseRules.relaxedComms)[0]}`
                          : 'This card cannot be a truthful hint'
                        : playable
                          ? 'Play this card'
                          : blockedReason(card)
                    }
                  />
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {confirmQuit ? (
        <ConfirmQuit
          onCancel={() => setConfirmQuit(false)}
          onConfirm={() => {
            setConfirmQuit(false);
            void crew.abort();
          }}
        />
      ) : null}

      {over && resultDismissed ? (
        <button className="result-reopen btn btn--primary" onClick={() => setResultDismissed(false)}>
          {game.phase === 'won' ? 'Mission accomplished' : 'Mission failed'}. Show result
        </button>
      ) : null}

      {over && !resultDismissed && !recap ? (
        <ResultOverlay
          crew={crew}
          game={game}
          isHost={snapshot.room.hostId === crew.playerId}
          nameOf={nameOf}
          onReview={() => setResultDismissed(true)}
        />
      ) : null}
    </div>
  );
}

function shortHint(hint: string): string {
  if (hint === 'highest') return 'high';
  if (hint === 'lowest') return 'low';
  if (hint === 'have') return 'have';
  return 'only';
}

const QUIPS_PLAY = [
  'do not choke',
  'pleaseeee',
  'trust me',
  'I have a plan',
  'this is fine',
  'watch this',
  'no pressure',
  'calculated',
  'I meant that',
  'uh oh',
  'beautiful',
  'hold on...',
  'engaging brain',
  'sorry in advance',
  'we are so back',
  'it is over',
  'trust the process',
  'do not look',
  'this is my roman empire',
  'send help',
  'I am cooking',
  'I am not cooking',
  'strategic',
  'improvising',
  'nailed it probably',
  'that felt wrong',
  'my hands slipped',
  'blame the deck',
  'I regret this',
  'zero notes',
  'chef kiss',
  'pray',
  'we ball',
  'lock in',
  'someone stop me',
  'this is my villain arc',
  'genuinely no idea',
  'vibes only',
  'the math is mathing',
  'the math is not mathing',
  'I fear no card',
  'this one is scary',
  'gently now',
  'here goes nothing',
  'wish me luck',
  'do not perceive me',
  'clutch incoming',
  'I peaked',
  'oops',
  'that is a card alright'
] as const;

const QUIPS_WIN = [
  'got it!',
  'mine!',
  'textbook',
  'as planned',
  'phew',
  'nailed it',
  'never doubted us',
  'easy',
  'too easy',
  'skill issue for you',
  'was that so hard',
  'flawless',
  'I am him',
  'built different',
  'certified',
  'get in',
  'and that is that',
  'no notes',
  'perfection',
  'told you so',
  'ez clap',
  'the plan worked',
  'somehow',
  'accidental genius',
  'we are cooking now'
] as const;

const QUIPS_POKED = [
  'ow! rude',
  'excuse me?',
  'hey!',
  'I felt that',
  'stop it',
  'not the poke',
  'assault',
  'do that again',
  'unprovoked',
  'I am telling',
  'why',
  'we are in space, be nice',
  'noted. remembered.',
  'that is going in the log',
  'poke me again coward',
  'ouch',
  'rude and uncalled for',
  'this is workplace harassment',
  'I was concentrating',
  'you will regret that'
] as const;

const QUIPS_POKER = [
  'poked!',
  'boop',
  'wake up',
  'your turn buddy',
  'hello?',
  'anyone home?',
  'tag',
  'gotcha'
] as const;

function pick<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)]!;
}

/** Three-dot pulse so a stalled table always reads as "waiting", not "broken". */
function WaitingDots() {
  return (
    <span className="waiting" aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

function ConfirmQuit({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="overlay" role="alertdialog" aria-label="Quit mission">
      <div className="overlay__card overlay__card--compact">
        <h2>Quit this mission?</h2>
        <p>
          The mission ends for the whole crew and everyone returns to the lobby. Anyone who is
          offline gets dropped so you can start again straight away.
        </p>
        <div className="overlay__actions">
          <button className="btn btn--ghost" onClick={onCancel}>
            Keep playing
          </button>
          <button className="btn btn--danger" onClick={onConfirm}>
            Quit mission
          </button>
        </div>
      </div>
    </div>
  );
}

function TaskBoard({
  game,
  nameOf,
  youId,
  crew
}: {
  game: GameView;
  nameOf: (id: string | null) => string;
  youId: string | null;
  crew: Crew;
}) {
  const drafting = game.phase === 'draft';
  const myDraftTurn = drafting && youId !== null && game.turnId === youId;
  const iAmCommander = youId !== null && game.commanderId === youId;
  // The random deal is the commander's own call, on the commander's own turn.
  const canAutoAssign = drafting && myDraftTurn && iAmCommander;
  const mineSoFar = game.tasks.filter((t) => t.ownerId === youId).length;
  const openCount = game.tasks.filter((t) => t.ownerId === null).length;
  const lastToPick = game.draftTurnsTaken >= game.players.length - 1;
  const [pending, setPending] = useState(false);
  const requestInFlight = useRef(false);

  // Do not send a second toggle or end the turn while a claim/release is in flight.
  const draftAction = async (action: () => Promise<void>) => {
    if (requestInFlight.current) return;
    requestInFlight.current = true;
    setPending(true);
    try {
      await action();
    } finally {
      requestInFlight.current = false;
      setPending(false);
    }
  };

  return (
    <section className="tasks" aria-label="Mission tasks">
      <div className="tasks__bar">
        <h3>Tasks</h3>
        {drafting ? (
          <>
            <span className="tasks__hint">
              {myDraftTurn
                ? `Claim tasks; click yours again to release them. ${openCount} left. Claiming the last task starts play immediately; otherwise end your turn when ready.`
                : `${nameOf(game.turnId)} is choosing`}
            </span>
            <button
              type="button"
              className="btn btn--small btn--primary"
              onClick={() => void draftAction(() => crew.endDraftTurn())}
              disabled={!myDraftTurn || pending}
              title={
                myDraftTurn
                  ? 'Hand the pick to the next pilot'
                  : 'Only on your turn'
              }
            >
              {mineSoFar > 0 ? 'Done picking' : 'Take none'}
            </button>
            <button
              type="button"
              className="btn btn--small btn--ghost"
              onClick={() => void draftAction(() => crew.autoAssignTasks())}
              disabled={!canAutoAssign || pending}
              title={
                canAutoAssign
                  ? 'Hand the rest of the tasks out at random'
                  : 'Only the commander, on their own turn'
              }
            >
              Deal them randomly
            </button>
            {myDraftTurn && lastToPick && !iAmCommander ? (
              <span className="tasks__warn">
                Anything left goes to {nameOf(game.commanderId)}.
              </span>
            ) : null}
            {myDraftTurn && iAmCommander && game.draftTurnsTaken > 0 ? (
              <span className="tasks__warn">You take whatever is left.</span>
            ) : null}
          </>
        ) : null}
      </div>
      <ul className="tasks__list">
        {game.tasks.map((task) => (
          <li key={task.id}>
            <TaskChip
              task={task}
              owner={nameOf(task.ownerId)}
              mine={task.ownerId !== null && task.ownerId === youId}
              claimable={myDraftTurn && (task.ownerId === null || task.ownerId === youId)}
              pending={pending}
              onClaim={() => void draftAction(() => crew.pickTask(task.id))}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function TaskChip({
  task,
  owner,
  mine,
  claimable,
  pending,
  onClaim
}: {
  task: TaskState;
  owner: string;
  mine: boolean;
  claimable: boolean;
  pending: boolean;
  onClaim: () => void;
}) {
  const done = task.completedAt !== null;
  const order =
    task.constraint.kind === 'sequence'
      ? `#${task.constraint.index}`
      : task.constraint.kind === 'final'
        ? 'last'
        : null;

  const who = done ? 'Complete' : task.ownerId ? (mine ? 'You' : owner) : 'Unclaimed';
  const sentence = done
    ? 'task complete'
    : mine && claimable
      ? 'tap to release'
      : task.ownerId
        ? 'must win this'
        : 'tap to claim';

  const body = (
    <>
      <PlayingCard card={task.card} size="sm" badge={order} muted={done} />
      <span className="task__text">
        <strong>{who}</strong>
        <small>{sentence}</small>
      </span>
    </>
  );

  const className = ['task', mine ? 'is-mine' : '', done ? 'is-done' : ''].filter(Boolean).join(' ');

  return claimable ? (
    <button
      type="button"
      className={`${className} is-claimable`}
      aria-pressed={mine}
      disabled={pending}
      onClick={onClaim}
    >
      {body}
    </button>
  ) : (
    <div className={className}>{body}</div>
  );
}

function ResultOverlay({
  crew,
  game,
  isHost,
  nameOf,
  onReview
}: {
  crew: Crew;
  game: GameView;
  isHost: boolean;
  nameOf: (id: string | null) => string;
  onReview: () => void;
}) {
  const won = game.phase === 'won';
  const mvp = [...game.players].sort((a, b) => b.tricksWon - a.tricksWon)[0];

  // A loss lands harder when it arrives in beats rather than all at once.
  const [stage, setStage] = useState(won ? 3 : 0);
  useEffect(() => {
    if (won) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setStage(3);
      return;
    }
    const timers = [
      window.setTimeout(() => setStage(1), 650),
      window.setTimeout(() => setStage(2), 1500),
      window.setTimeout(() => setStage(3), 2400)
    ];
    return () => timers.forEach(window.clearTimeout);
  }, [won]);
  const shown = (at: number) => `reveal ${stage >= at ? 'is-in' : ''}`;

  return (
    <div className={`overlay overlay--result ${won ? 'is-win' : 'is-loss'}`} role="alertdialog">
      {won ? <Fireworks /> : <Blast />}
      <div className="overlay__card">
        <p className={`stamp ${shown(1)}`}>{won ? 'Mission Accomplished' : 'Mission Failed'}</p>
        <h2 className={shown(2)}>
          {game.mission.id}. {game.mission.name}
        </h2>
        <p className={`overlay__reason ${shown(2)}`}>{game.result?.reason}</p>
        {game.lastTrick ? (
          <p className="result__last-play">
            {game.lastTrick.plays.map((play) => `${nameOf(play.playerId)} played ${play.card.suit} ${play.card.rank}`).join(' · ')}.
            {' '}{nameOf(game.lastTrick.winnerId)} won trick {game.lastTrick.number}.
          </p>
        ) : null}

        <dl className={`overlay__stats ${shown(3)}`}>
          <div>
            <dt>Tasks</dt>
            <dd>
              {game.completedTasks}/{game.tasks.length}
            </dd>
          </div>
          <div>
            <dt>Tricks</dt>
            <dd>{Math.max(0, game.trickNumber - 1)}</dd>
          </div>
          <div>
            <dt>Attempt</dt>
            <dd>{game.attempt}</dd>
          </div>
          <div>
            <dt>Most tricks</dt>
            <dd>{mvp ? nameOf(mvp.id) : 'none'}</dd>
          </div>
        </dl>

        {isHost ? (
          <div className={`overlay__actions ${shown(3)}`}>
            <button className="btn" onClick={() => void crew.restart(false)}>
              Retry mission {game.mission.id}
            </button>
            {won ? (
              <button className="btn btn--primary" onClick={() => void crew.restart(true)}>
                Next mission
              </button>
            ) : null}
            <button className="btn btn--ghost" onClick={onReview}>
              Review the board
            </button>
            <button className="btn btn--ghost" onClick={() => void crew.abort()}>
              Back to lobby
            </button>
          </div>
        ) : (
          <div className={`overlay__actions ${shown(3)}`}>
            <button className="btn btn--ghost" onClick={onReview}>
              Review the board
            </button>
            <p className="overlay__waiting">Waiting for the host to choose what is next&hellip;</p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Debris blowing outward, so a failed mission lands as an event. */
function Blast() {
  return (
    <div className="blast" aria-hidden="true">
      <span className="blast__core" />
      <span className="blast__ring" />
      <span className="blast__ring blast__ring--late" />
      {Array.from({ length: 14 }).map((_, i) => (
        <span
          key={i}
          className="blast__shard"
          style={{
            transform: `rotate(${(360 / 14) * i}deg)`,
            animationDelay: `${(i % 5) * 30}ms`
          }}
        />
      ))}
    </div>
  );
}

/** Rising sparks for a completed mission. */
function Fireworks() {
  return (
    <div className="sparks" aria-hidden="true">
      {Array.from({ length: 18 }).map((_, i) => (
        <span
          key={i}
          className="sparks__dot"
          style={{
            left: `${(i * 5.5 + 4) % 100}%`,
            animationDelay: `${(i % 6) * 140}ms`,
            animationDuration: `${2.2 + (i % 4) * 0.4}s`
          }}
        />
      ))}
    </div>
  );
}
